/**
 * TPO (Time Price Opportunity) binning — the Market Profile counter.
 *
 * A TPO profile answers a different question from a volume profile: not "how
 * much traded at this price" but "how many TIME BRACKETS visited this price".
 * Classically the session is cut into 30-minute brackets (lettered A, B, C…)
 * and a row earns one mark per bracket that traded there.
 *
 * That difference matters for accuracy. Volume-at-price from OHLC bars has to
 * GUESS a distribution inside each bar (see volumeProfile.ts, which spreads
 * uniformly). TPO has no such guess to make: a bar's high/low directly answers
 * "did price trade at this level during this bracket?". So a TPO profile built
 * from OHLC bars is faithful, not approximate — the one meaningful advantage it
 * has over its volume sibling.
 *
 * Two structural departures from volumeProfile.ts:
 *  - Rows are a FIXED price increment (tick size), not N equal buckets. Blocks
 *    are discrete units, so rows must be comparable across sessions.
 *  - Each row keeps the IDENTITY of the brackets that visited it, not just a
 *    tally, because the renderer colours each block by when it happened.
 *
 * Pure and klinecharts-free so it unit-tests in isolation (binVolumeByPrice
 * precedent). POC and value area come from the shared profileValueArea module.
 */

import { expandValueArea, pointOfControl } from './profileValueArea'
import { REGULAR_OPEN_MINUTES, sessionClock } from './sessionFilter'

/** The only bar fields TPO needs — no volume, no open/close. */
export interface TpoBar {
  timestamp: number
  low: number
  high: number
}

/** Default bracket width: the classic Market Profile half hour. */
const DEFAULT_BRACKET_MINUTES = 30

/** Row-count ceiling. A fine tick over a wide range would otherwise produce
 *  tens of thousands of unreadable 0-px rows and a pathological render. */
const DEFAULT_MAX_ROWS = 240

/**
 * Slack for the rows-per-domain division. Price arithmetic is binary floating
 * point, so an exact fit reads as a hair over: `10.4 - 10.0` is
 * 0.40000000000000036, and a naive ceil of `domain / 0.1` yields 5 rows for a
 * range that holds exactly 4. Without this, most real profiles would carry a
 * spurious empty top row.
 */
const ROW_FIT_EPSILON = 1e-9

export interface TpoRow {
  /** Inclusive lower price edge. */
  priceLow: number
  /** Exclusive upper price edge (the top row is inclusive). */
  priceHigh: number
  /** Number of distinct brackets that traded in this row — the TPO count. */
  count: number
  /** Which brackets, ascending. Drives the renderer's time gradient. */
  brackets: number[]
}

export interface TpoProfile {
  rows: TpoRow[]
  /** Index of the most-visited row (point of control). */
  pocIdx: number
  /** Value-area high edge — highest-priced row inside the value area. */
  vahIdx: number
  /** Value-area low edge — lowest-priced row inside the value area. */
  valIdx: number
  /**
   * How many brackets the session SPANNED — `bracketMax - bracketMin + 1`, the
   * gradient's domain and expanded mode's column count.
   *
   * A span rather than a max because brackets go negative once they are anchored
   * to the regular open: pre-market sits below zero.
   */
  bracketCount: number
  /**
   * Lowest bracket index present, 0 for a regular-hours-only session and
   * negative when pre-market is included.
   *
   * Both the gradient and expanded mode's x-offset subtract it, so a pre-market
   * block lands inside the session's span instead of left of it.
   */
  bracketMin: number
  /** Price increment actually used, after any max-rows coarsening. */
  tickSize: number
  /**
   * Lowest row edge — the grid origin, already snapped to a tick multiple when
   * `gridAligned`. Exposed so a companion profile (the volume band beside a TPO)
   * can bin onto exactly these rows instead of its own division of the domain.
   */
  gridOrigin: number
  /**
   * Bracket counts per segment, for the letter case split. `null` when the
   * caller did not supply one, in which case letters run A–Z then a–z.
   */
  bracketScheme: BracketScheme | null
  /**
   * Initial balance — the price range of the first TWO brackets (the opening
   * hour at the classic 30-minute bracket). `null` when the session has only one
   * bracket, where an "opening range" would just restate the whole session.
   */
  initialBalance: { low: number; high: number } | null
}

export interface TpoOptions {
  /** Price increment per row. Coarsened if it would exceed `maxRows`. */
  tickSize: number
  /** Minutes per bracket (default 30). */
  bracketMinutes?: number
  /** Share of total TPOs the value area must cover (default 70). */
  valueAreaPct?: number
  /** Row-count ceiling (default 240). */
  maxRows?: number
  /**
   * Snap row edges to absolute multiples of `tickSize` instead of starting at the
   * session's own low. Default true.
   *
   * Without this the row origin is `min(low)`, so any change to the bar set moves
   * EVERY row edge — which made the profile jump on every scrolled bar. Snapping
   * to a global grid also makes rows line up between sessions, so a level means
   * the same thing across days.
   */
  gridAligned?: boolean
  /**
   * Timestamp bracket numbering counts from — the session's TRUE open, which may
   * be scrolled off-screen. Defaults to the first bar's timestamp.
   *
   * Needed because the caller passes only the bars in view: without it, bracket
   * `A` would silently become "the first bracket you happened to scroll to"
   * rather than the opening bracket, making the letters and the initial balance
   * lie about the session.
   */
  sessionOpen?: number
  /**
   * Bracket index for a timestamp, overriding the `sessionOpen` delta.
   *
   * Supplied by the indicator as `createRthBracketOf`, which anchors bracket 0 to
   * the 09:30 clock rather than to the first bar carrying data. May return
   * negative indices for pre-market.
   */
  bracketOf?: (timestamp: number) => number
  /** Bracket counts per segment, stored on the profile for the letter casing. */
  bracketScheme?: BracketScheme
}

/** Row count an auto-derived tick aims for — dense enough to show structure,
 *  sparse enough that blocks stay legible at normal zoom. */
const AUTO_TARGET_ROWS = 60

/** How many recent sessions the row size is measured over. */
const RANGE_LOOKBACK_SESSIONS = 20

/**
 * How many rows a TYPICAL session should span.
 *
 * 25, calibrated against a reference chart: on the same instrument and day it showed 23
 * rows at its default row width, while this ran 46 at a target of 60. A target of
 * 25 lands at 20–25 rows across price scales from $0.20 to $18 of daily range,
 * centred near 23.
 *
 * Because the snap rounds UP, the realised count is always at or below the
 * target — so this reads as a ceiling, not an average. Nudging it does nothing
 * until it crosses a rung, then moves in a step, so changes here are worth
 * checking against real ranges rather than assumed proportional.
 *
 * The row size is volatility-derived, so this is the only knob that decides
 * resolution: a normal day gets about this many rows, a quiet day fewer, a wild
 * day more. That variation is correct — it is the truth about the day — and it is
 * why row size is a setting in every Market Profile implementation.
 */
const TARGET_ROWS_PER_SESSION = 25

/**
 * A "nice" price increment for a range, when the caller has no real tick size.
 *
 * There is no tick size anywhere in the chart data, and instruments span four
 * orders of magnitude here (a sub-$1 small cap to a $65k index), so a fixed
 * increment cannot work. This targets ~60 rows and then snaps to a 1/2/5×10ⁿ
 * step so rows land on round prices a trader would actually quote.
 */
export function autoTickSize(priceMin: number, priceMax: number): number {
  const domain = priceMax - priceMin
  if (!Number.isFinite(domain) || domain <= 0) return 0.01
  return snapToNiceStep(domain / AUTO_TARGET_ROWS)
}

/**
 * Rungs the row size may land on, per decade — all still prices a trader quotes.
 *
 * A bare 1/2/5 ladder is too coarse for this job. It only snaps UP, and the
 * overshoot varies enormously with where the raw value falls: a raw 0.0092 lands
 * on 0.01 (+9%) while a raw 0.117 lands on 0.20 (+71%). So Auto missed its own
 * row target by wildly different amounts depending on the instrument's price
 * scale — 55 rows on a $3 small cap but only 34 on a $200 name, from the same
 * target of 60. Adding the intermediate rungs cuts the worst-case overshoot from
 * 2x to 1.5x, so Auto lands near its target consistently.
 */
const NICE_RUNGS = [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10] as const

/**
 * Snap a raw increment up to the nearest nice step, so rows land on prices a
 * trader would actually quote.
 */
function snapToNiceStep(raw: number): number {
  if (!Number.isFinite(raw) || raw <= 0) return 0.01
  const magnitude = 10 ** Math.floor(Math.log10(raw))
  const normalized = raw / magnitude
  // Epsilon-tolerant, for the same reason as ROW_FIT_EPSILON: `0.005 / 0.001` is
  // 5.000000000000001, so a bare comparison would skip a rung and silently
  // enlarge the row exactly at the boundaries these rungs exist to hit.
  for (const rung of NICE_RUNGS) {
    if (normalized <= rung + ROW_FIT_EPSILON) return rung * magnitude
  }
  return 10 * magnitude
}

/**
 * Row size from recent session ranges — the volatility anchor.
 *
 * Measured over each session's own high-low, NOT the gap-inclusive true range: a
 * small cap that gaps 100% overnight would otherwise get very coarse intraday
 * rows, and an overnight gap says nothing about structure WITHIN a session, which
 * is all a TPO profile describes.
 *
 * Uses the MEDIAN rather than ATR's Wilder mean, deliberately. These instruments
 * routinely print a runner day an order of magnitude above normal, and a mean
 * lets that single day coarsen every other session's rows — measured, one 5.0 day
 * among thirteen 0.5 days doubled the row size from 0.01 to 0.02. The median
 * leaves the typical day untouched and simply gives the runner more rows, which
 * is the honest outcome.
 *
 * Depends only on the data, never the viewport, so the binning — and therefore the
 * POC/VAH/VAL prices — cannot move when you zoom or scroll.
 *
 * `ranges` is oldest-first, one entry per session. Returns 0 when there is
 * nothing usable, letting the caller fall back.
 */
export function rowSizeFromSessionRanges(
  ranges: readonly number[],
  targetRows: number = TARGET_ROWS_PER_SESSION,
): number {
  const usable = ranges.filter((r) => Number.isFinite(r) && r > 0)
  if (usable.length === 0) return 0
  const recent = usable.slice(-RANGE_LOOKBACK_SESSIONS).sort((a, b) => a - b)
  const mid = recent.length >> 1
  const median = recent.length % 2 === 1 ? recent[mid] : (recent[mid - 1] + recent[mid]) / 2
  if (!(median > 0)) return 0
  return snapToNiceStep(median / Math.max(1, targetRows))
}

/**
 * Which bracket a timestamp falls in, counting from `sessionOpen`.
 *
 * Anchored to the session open rather than to wall-clock boundaries: Market
 * Profile's bracket A is the first half hour OF THE SESSION, so a 09:47 open
 * puts 09:47–10:16 in bracket 0. Flooring to wall-clock :00/:30 instead would
 * split the opening range across two brackets and misstate the profile.
 * Pre-open timestamps clamp into bracket 0.
 */
export function bracketIndexOf(
  timestamp: number,
  sessionOpen: number,
  bracketMinutes: number = DEFAULT_BRACKET_MINUTES,
): number {
  const span = Math.max(1, bracketMinutes) * 60_000
  const delta = timestamp - sessionOpen
  if (delta <= 0) return 0
  return Math.floor(delta / span)
}

/** How many brackets each segment of the trading day holds. */
export interface BracketScheme {
  /** Brackets in the regular session — the uppercase run, starting at A. */
  rthBrackets: number
  /** Brackets between the extended open (04:00) and the regular open (09:30). */
  preBrackets: number
}

/**
 * Bracket counts per segment, derived from the bracket width.
 *
 * Lets the letter assignment tell regular-session brackets from extended ones
 * using the INDEX alone, with no per-bar timestamp lookup in the renderer.
 */
export function bracketSchemeFor(
  bracketMinutes: number = DEFAULT_BRACKET_MINUTES,
  earlyClose: boolean = false,
): BracketScheme {
  const width = Math.max(1, bracketMinutes)
  // 09:30 → 16:00 normally, 09:30 → 13:00 on a half day.
  const rthMinutes = earlyClose ? 780 - 570 : 960 - 570
  return {
    rthBrackets: Math.ceil(rthMinutes / width),
    // 04:00 → 09:30. Unaffected by an early close.
    preBrackets: Math.ceil((570 - 240) / width),
  }
}

/**
 * Day number for a 'YYYY-MM-DD' key, for date-only arithmetic.
 *
 * Returns NaN for anything unparseable rather than a plausible-looking number.
 * The caller turns that into a fallback: a NaN day number would otherwise
 * propagate into every bracket index and silently corrupt a whole profile —
 * `Math.floor(NaN)` is NaN, `Set.has(NaN)` is its own bucket, and the render
 * would come out empty with no clue why.
 */
function dayNumberOf(dateStr: string): number {
  const parts = dateStr.split('-')
  if (parts.length !== 3) return Number.NaN
  const [y, m, d] = parts.map(Number)
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return Number.NaN
  if (m < 1 || m > 12 || d < 1 || d > 31) return Number.NaN
  return Date.UTC(y, m - 1, d) / 86_400_000
}

/**
 * Bracket indexer anchored to the REGULAR-session open (09:30 exchange-local),
 * not to the first bar that happens to carry data.
 *
 * This is what makes the letter grid stable. The previous anchor was
 * `timestamps[run.startIdx]` — the session's first bar — so on a thin small cap
 * whose first pre-market print landed at 04:40, that print became bracket `A`
 * and every regular-session letter shifted. Two tickers on the same day, or the
 * same ticker on two days, disagreed about what `A` meant. Anchoring to the
 * clock makes bracket 0 always the 09:30 half hour; pre-market goes negative.
 *
 * Works off exchange-local minute-of-day plus a whole-day shift rather than a
 * raw timestamp delta, so it stays correct across DST — a UTC delta from a fixed
 * anchor would be an hour out for half the year. The day shift also keeps the
 * `prior-after-hours` anchor ordered, where a run holds yesterday's after-hours
 * (minute-of-day 1080) ahead of today's pre-market (240).
 *
 * `anchorDateStr` is the session's regular-session date, 'YYYY-MM-DD' — exactly
 * what `sessionDayKey` returns for either anchor mode.
 */
export function createRthBracketOf(
  anchorDateStr: string,
  bracketMinutes: number,
  timezone: string,
): (timestamp: number) => number {
  const width = Math.max(1, bracketMinutes)
  const anchorDay = dayNumberOf(anchorDateStr)
  return (timestamp: number): number => {
    const { minutes, dateStr } = sessionClock(timestamp, timezone)
    const barDay = dayNumberOf(dateStr)
    // Both day numbers must resolve for the shift to mean anything. Falling back
    // to a same-day assumption keeps the profile rendering with sane letters
    // rather than collapsing to NaN brackets.
    const shift =
      Number.isFinite(barDay) && Number.isFinite(anchorDay) ? (barDay - anchorDay) * 1440 : 0
    return Math.floor((shift + minutes - REGULAR_OPEN_MINUTES) / width)
  }
}

/**
 * Build a TPO profile from one session's bars.
 *
 * Bars are assumed ascending by timestamp; the FIRST bar's timestamp is taken as
 * the session open (the bracket anchor), so callers pass one session's slice —
 * the same shape `sessionRuns`/`expandToFullDay` already produce for the session
 * volume profile.
 *
 * Returns null when there is nothing meaningful to draw: no bars, or a
 * zero-width price domain.
 */
export function buildTpoProfile(bars: TpoBar[], opts: TpoOptions): TpoProfile | null {
  if (bars.length === 0) return null

  const bracketMinutes = opts.bracketMinutes ?? DEFAULT_BRACKET_MINUTES
  const valueAreaPct = opts.valueAreaPct ?? 70
  const maxRows = Math.max(1, Math.floor(opts.maxRows ?? DEFAULT_MAX_ROWS))

  let priceMin = Infinity
  let priceMax = -Infinity
  for (const b of bars) {
    if (b.low < priceMin) priceMin = b.low
    if (b.high > priceMax) priceMax = b.high
  }
  if (!Number.isFinite(priceMax - priceMin) || priceMax - priceMin <= 0) return null

  let tickSize = opts.tickSize > 0 ? opts.tickSize : (priceMax - priceMin) / maxRows

  // The session's true extent, kept so the OUTER edges can clamp back to it.
  const sessionLow = priceMin
  const sessionHigh = priceMax

  // Snap the grid origin DOWN to a tick multiple so row boundaries are absolute
  // rather than relative to whichever bars happen to be in the set.
  if (opts.gridAligned !== false) {
    priceMin = Math.floor(priceMin / tickSize + ROW_FIT_EPSILON) * tickSize
  }

  const domain = priceMax - priceMin
  if (!Number.isFinite(domain) || domain <= 0) return null

  // Coarsen the increment rather than emitting more rows than we can draw. The
  // row count is what bounds the render, so the tick bends to it.
  let rowCount = Math.ceil(domain / tickSize - ROW_FIT_EPSILON)
  if (rowCount > maxRows) {
    tickSize = (priceMax - priceMin) / maxRows
    rowCount = maxRows
  }
  rowCount = Math.max(1, rowCount)

  /**
   * Interior row boundaries sit on the absolute grid; the OUTER two clamp to the
   * session's real low and high.
   *
   * Both properties are needed and they pull in opposite directions. Grid
   * alignment is what stops the profile jumping when the bar set changes (the row
   * origin used to be `min(low)`, so every edge moved). Clamping the outer edges
   * is what stops blocks rendering past prices that never traded — `rowCount` is a
   * ceil, and the grid origin sits BELOW the session low by up to a tick, so
   * unclamped the profile would overshoot at both ends.
   */
  const rows: TpoRow[] = Array.from({ length: rowCount }, (_, i) => ({
    priceLow: i === 0 ? sessionLow : priceMin + i * tickSize,
    priceHigh: i === rowCount - 1 ? sessionHigh : priceMin + (i + 1) * tickSize,
    count: 0,
    brackets: [],
  }))

  // Row index for a price, with the top edge folding into the last row.
  const rowIndexOf = (price: number): number => {
    const idx = Math.floor((price - priceMin) / tickSize)
    return idx < 0 ? 0 : idx >= rowCount ? rowCount - 1 : idx
  }

  // Per row, the brackets already counted — so a bracket that spans several bars
  // at the same price still contributes exactly one TPO. This is the core
  // semantic difference from volume binning, which sums every bar.
  const seen: Array<Set<number>> = rows.map(() => new Set())

  const sessionOpen = opts.sessionOpen ?? bars[0].timestamp
  const bracketOf =
    opts.bracketOf ?? ((ts: number) => bracketIndexOf(ts, sessionOpen, bracketMinutes))
  let maxBracket = -Infinity
  let minBracket = Infinity
  // Initial balance: the price range of brackets 0 and 1 only.
  let ibLow = Infinity
  let ibHigh = -Infinity
  let sawSecondBracket = false

  for (const b of bars) {
    const bracket = bracketOf(b.timestamp)
    if (bracket > maxBracket) maxBracket = bracket
    if (bracket < minBracket) minBracket = bracket
    // `>= 0` matters once brackets are anchored to the open: without it every
    // pre-market bar folded into the initial balance, so IB reported the
    // pre-market range instead of the classic opening hour.
    if (bracket >= 0 && bracket <= 1) {
      if (b.low < ibLow) ibLow = b.low
      if (b.high > ibHigh) ibHigh = b.high
      if (bracket === 1) sawSecondBracket = true
    }
    const first = rowIndexOf(b.low)
    const last = rowIndexOf(b.high)
    for (let i = first; i <= last; i++) {
      if (seen[i].has(bracket)) continue
      seen[i].add(bracket)
      rows[i].count += 1
      rows[i].brackets.push(bracket)
    }
  }

  // Bars arrive in ascending time, so `brackets` is already ascending — the
  // renderer relies on that for its gradient, so assert it cheaply by sorting
  // only when a row is out of order (effectively free on ordered input).
  for (const row of rows) {
    for (let i = 1; i < row.brackets.length; i++) {
      if (row.brackets[i] < row.brackets[i - 1]) {
        row.brackets.sort((a, b) => a - b)
        break
      }
    }
  }

  const totals = rows.map((r) => r.count)
  const totalTpos = totals.reduce((sum, n) => sum + n, 0)
  if (totalTpos === 0) return null
  const pocIdx = pointOfControl(totals)
  const { valIdx, vahIdx } = expandValueArea(totals, pocIdx, (valueAreaPct / 100) * totalTpos)

  // Requires BOTH opening brackets: with only bracket 0 present the "opening
  // range" would just restate that bracket, which is not an initial balance.
  const initialBalance = sawSecondBracket && ibHigh > ibLow ? { low: ibLow, high: ibHigh } : null

  // Every bar contributed a bracket (totalTpos > 0 above), so these are finite.
  const bracketMin = Number.isFinite(minBracket) ? minBracket : 0
  const bracketMax = Number.isFinite(maxBracket) ? maxBracket : 0

  return {
    rows,
    pocIdx,
    vahIdx,
    valIdx,
    bracketCount: bracketMax - bracketMin + 1,
    bracketMin,
    tickSize,
    gridOrigin: priceMin,
    bracketScheme: opts.bracketScheme ?? null,
    initialBalance,
  }
}
